import type { Locales } from '@/types/project-types'
import type { AddToCartTypes } from '@maestro/commerce-blocks/components/product-card/product-card.types'
import type {
  CheckoutType,
  CmsImages,
} from '@maestro/commerce-blocks/theme/theme.types'

import commerceBlocks from '@maestro/commerce-blocks/theme/theme'
import { envNumber } from '@maestro/core/config/env'

/*
 * Theming related configurations.
 *
 * DO NOT USE IT FOR SENSITIVE DATA!
 * Use only `NEXT_PUBLIC_*` environment variables.
 */
type LocalThemeType = Omit<typeof commerceBlocks, 'site' | 'search'> & {
  site: Omit<typeof commerceBlocks.site, 'locale'> & {
    locale: Locales
    supportedLocales: { code: Locales; label: string }[]
  }
  search: typeof commerceBlocks.search & {
    isVisibleTabs: boolean
  }
}

const urls = {
  ...commerceBlocks.urls,
}

const localTheme: LocalThemeType = {
  ...commerceBlocks,
  site: {
    ...commerceBlocks.site,
    title: 'Polar',
    locale: (process.env.NEXT_PUBLIC_LOCALE || 'en-US') as Locales,
    timeZone: process.env.NEXT_PUBLIC_SITE_TIMEZONE || 'GMT',
    supportedLocales: [
      { code: 'en-US', label: 'English' },
      { code: 'de-DE', label: 'German' },
      { code: 'fr-FR', label: 'French' },
      { code: 'it-IT', label: 'Italian' },
      { code: 'fi-FI', label: 'Finnish' },
      { code: 'es-ES', label: 'Spanish' },
    ],
    logo: {
      width: 152,
      height: 25,
    },
    checkoutType: 'internal' as CheckoutType,
  },
  priceConfig: {
    ...commerceBlocks.priceConfig,
    currencyIso: process.env.NEXT_PUBLIC_SITE_CURRENCY || 'EUR',
    showFormattedPrice: true,
    showTaxLabel: false,
  },
  header: {
    ...commerceBlocks.header,
    companySwitcher: {
      searchEnabled: true,
    },
    userInfo: {
      ...commerceBlocks.header.userInfo,
      isEnabledSignOut: true,
    },
  },
  freeShippingBar: {
    isVisibleFreeShippingBar: false,
  },
  usp: {
    isUspEnabled: false,
  },
  recommendations: {
    isRecommendationsEnabled: false,
  },
  search: {
    ...commerceBlocks.search,
    isVisibleTabs: false,
    resultsPerPageSearch: 30, // Maestro override of default 120
    resultsPerPagePLP: 30, // Maestro override of default 120
    popularSuggestions: envNumber(
      process.env.NEXT_PUBLIC_SEARCH_SUGGESTIONS_POPULAR,
      commerceBlocks.search.popularSuggestions
    ),
  },
  cart: {
    ...commerceBlocks.cart,
    isClickAndCollectEnabled: false,
    isCouponCodesEnabled: false,
    showDeliveryMethodsTabsTop: true,
    useLowerCaseVariantName: false,
  },
  images: {
    ...commerceBlocks.images,
    converter: process.env.NEXT_PUBLIC_MAESTRO_IO!,
    cartList: {
      width: 116,
      height: 116,
      srcSet: [116, 232],
      sizes: [{ size: '116px' }],
    },
    cms: {
      ...commerceBlocks.images.cms,
    } satisfies CmsImages,
  },
  accountPage: {
    ...commerceBlocks.accountPage,
    menuItems: [
      {
        labelKey: 'account.orders',
        url: urls.orders,
        icon: 'package',
      },
      {
        labelKey: 'account.favorites',
        url: urls.wishlist,
        icon: 'heart-empty',
      },
      {
        labelKey: 'account.profile',
        url: urls.profile,
        icon: 'user',
      },
    ],
    orderDetails: {
      ...commerceBlocks.accountPage.orderDetails,
      orderSummaryPositionEnabled: true,
      orderSummaryInvoicingDetailsEnabled: true,
      isEnabledDeliveryStatusSplit: true,
      b2bProductList: {
        columns: [
          { id: 'product' },
          { id: 'deliveryDate' },
          { id: 'price' },
          { id: 'quantity' },
          { id: 'total' },
        ],
      },
      invoiceList: {
        ...commerceBlocks.accountPage.orderDetails.invoiceList,
        // Polar lists SAP documents, which carry no date and no amount.
        columns: [{ id: 'invoiceNumber' }, { id: 'download' }],
      },
      b2bAddress: {
        footerFields: [{ id: 'countryName' }],
      },
      progressSteps: [],
    },
    orderHistory: {
      ...commerceBlocks.accountPage.orderHistory,
      withProductImages: false,
      filterBar: {
        filters: [{ id: 'search' }, { id: 'dateRange' }, { id: 'option' }],
      },
    },
  },
  filter: {
    ...commerceBlocks.filter,
    priceFacet:
      process.env.NEXT_PUBLIC_FILTER_PRICE_FACET || 'price.NOK.default',
  },
  cms: {
    ...commerceBlocks.cms,
    navigation: {
      ...commerceBlocks.cms.navigation,
      header:
        process.env.NEXT_PUBLIC_HEADER_NAVIGATION ||
        commerceBlocks.cms.navigation.header,
    },
    // Polar: no order limit, the cap only guards against typos (POL-212).
    quantityCounterMaxValue: 9999,
  },
  product: {
    ...commerceBlocks.product,
    isReviewsEnabled: false,
    isVisibleBrand: false,
    isOutOfStockNotificationEnabled: false,
    identifierItemsPDP: [
      { labelKey: 'product.product-id-shorthand', field: 'id' },
      { labelKey: 'product.product-ean-shorthand', field: 'ean' },
    ],
    details: {
      ...commerceBlocks.product.details,
      cmsSections: [],
      displayProductRatingBelowTitle: false,
      variants: {
        ...commerceBlocks.product.details.variants,
        colorAttributeQualifier: 'polarColorVariantCategory',
        enableMultiVariant: true,
        variantDetailsFields: [
          'name',
          'shortDescription',
          'classifications',
          'gallery',
          'stock',
          'productReferences',
          'ean',
          'id',
        ],
      },
    },
    addToCartDisplayType: {
      productListing: 'icon',
      productList: 'icon',
      searchTopFoundProducts: 'none',
      cmsProducts: 'icon',
      wishlist: 'icon',
      wishlistList: 'icon',
      recommendations: 'icon',
      recommendationsList: 'icon',
    } as Record<string, AddToCartTypes>,
    isAddToCartProductStockValidationEnabled: false,
    displayStockStatus: {
      productCardDefault: true,
      cartItemDontForget: true,
      cmsProducts: true,
      recommendations: true,
      recommendationsList: true,
      searchTopFoundProducts: true,
      wishlist: true,
      productList: true,
      searchProductList: true,
    },
    enableClientSideStockFetching: {
      details: false,
      cartItemDontForget: true,
      cmsProducts: false,
      recommendations: true,
      recommendationsList: true,
      wishlist: false,
      productList: true,
      searchProductList: true,
    },
    displayPrice: {
      productCardDefault: true,
      cartItemDontForget: true,
      cmsProducts: true,
      recommendations: true,
      recommendationsList: true,
      searchTopFoundProducts: true,
      wishlist: false,
      productList: true,
      searchProductList: true,
    },
    enableClientSidePriceFetching: {
      details: false,
      cartItemDontForget: true,
      cmsProducts: false,
      recommendations: true,
      recommendationsList: true,
      wishlist: false,
      productList: true,
      searchProductList: true,
    },
  },
  loginPage: {
    ...commerceBlocks.loginPage,
    isSlidein: false,
  },
  afterLogin: {
    ...commerceBlocks.afterLogin,
    redirectToUrl: commerceBlocks.urls.home,
  },
  profilePage: {
    ...commerceBlocks.profilePage,
    isReadOnly: true,
  },
  internalCheckout: {
    ...commerceBlocks.internalCheckout,
    sections: [
      'formFields',
      'deliveryAddress',
      'invoicingAddress',
      'productList',
      'summary',
    ],
    formFields: {
      fields: [
        { id: 'customer', isRequired: true, isDisabled: true },
        {
          id: 'orderConfirmationEmail',
          isRequired: true,
          isEmailPrefilled: true,
        },
        { id: 'reference', isRequired: true },
        { id: 'deliveryDate', isRequired: true },
        { id: 'comment', isRequired: false },
      ],
    },
    deliveryAddress: { isChangeable: true },
    invoicingAddress: { isChangeable: true },
    deliveryOptions: { options: [] },
    summary: {
      terms: {
        isCheckboxRequired: true,
        cmsBlockKey: 'checkout-terms-cms-block',
        dialogId: 'terms-and-conditions',
      },
    },
  },
  colorConfig: {
    ...commerceBlocks.colorConfig,
    colorPreviewMapping: {
      ...commerceBlocks.colorConfig.colorPreviewMapping,
      brown: { name: 'Brown', value: 'rgb(165, 42, 42)' },
      gold: { name: 'Gold', value: 'rgb(255, 215, 0)' },
    },
  },
}

function theme(): LocalThemeType {
  return localTheme
}

export { theme }
